import { Component, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgClass } from '@angular/common';
import { EntityFieldHelpService } from '../../../services/entity-field-help.service';
import { IEntityFieldHelp } from '../../../../shared/models/info/entity-field-help';
import { HelpOpenIconComponent } from '../../icons/help-open-icon/help-open-icon.component';
import { EditComponent } from '../../icons/edit/edit.component';
import { CancelIconComponent } from '../../icons/cancel-icon/cancel-icon.component';
import { FormsModule } from '@angular/forms';
import { FloppyIconComponent } from '../../icons/floppy-icon/floppy-icon.component';
import { AccessRightsService } from '../../../services/access-rights.service';
import { Policy } from '../../../../shared/models/info/policy';
import { AppInsightsService } from '../../../services/app-insights.service';
import { AgentActionDirective } from '../../../directives/agent-action.directive';

@Component({
  selector: 'app-label-with-help',
  imports: [
    NgClass,
    HelpOpenIconComponent,
    EditComponent,
    CancelIconComponent,
    FormsModule,
    FloppyIconComponent,
    AgentActionDirective,
  ],
  templateUrl: './label-with-help.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './label-with-help.scss',
})
export class LabelWithHelp implements OnInit {
  @Input() public for!: string;
  @Input() public class!: string;
  @Input() public iconColor = 'text-gray-500';
  @Input() public fieldConfigurationAddress?: string;
  @Input() public helpText?: string;
  @Input() public updatedHelpText?: string;

  public readonly textAreaId = `help-edit-${crypto.randomUUID()}`;
  public iconVisible = false;
  public helpVisible = false;
  public editMode = false;
  public canEdit = false;

  private entityFieldHelpService = inject(EntityFieldHelpService);
  private accessRightsService = inject(AccessRightsService);
  private appInsightsService = inject(AppInsightsService);

  public ngOnInit(): void {
    this.accessRightsService.getAccessRights$().subscribe((ar) => {
      this.canEdit = ar.authorized(Policy.canUpsertFieldHelp);
      this.initializeHelp();
    });
  }

  public toggleHelp(): void {
    if (this.editMode) {
      return;
    }
    this.helpVisible = !this.helpVisible;
  }

  public startEditing(): void {
    this.editMode = true;
    this.helpVisible = false;
  }

  public cancelEditing(): void {
    this.editMode = false;
    this.helpVisible = true;
    this.updatedHelpText = this.helpText;
  }

  public upsertHelpText(): void {
    const [entityName, fieldName] = this.fieldConfigurationAddress!.split('.');
    const newFieldHelp = {
      fieldName: fieldName,
      entityName: entityName,
      helpText: this.updatedHelpText,
    } as IEntityFieldHelp;
    this.entityFieldHelpService.upsertEntityFieldHelp(newFieldHelp);

    this.helpText = this.updatedHelpText;
    this.editMode = false;
    this.helpVisible = true;
  }

  private initializeHelp(): void {
    if (this.fieldConfigurationAddress) {
      this.entityFieldHelpService.getEntityFieldHelp$().subscribe((entityFieldHelp) => {
        this.helpText = this.filterFieldHelpItems(entityFieldHelp)?.helpText;
        this.updatedHelpText = this.helpText;
        this.iconVisible = !!this.helpText || this.canEdit;
      });
    } else {
      this.iconVisible = this.canEdit;
    }
  }

  private filterFieldHelpItems(entityFieldHelpItems: IEntityFieldHelp[]): IEntityFieldHelp | undefined {
    if (!this.fieldConfigurationAddress) {
      return undefined;
    }

    let [entityName, fieldName] = this.fieldConfigurationAddress.split('.');
    if (!entityName || !fieldName) {
      console.error(`fieldConfigurationAddress ${this.fieldConfigurationAddress} invalid`);
      return undefined;
    }

    entityName = entityName.toUpperCase();
    fieldName = fieldName.toUpperCase();

    const fieldHelp = entityFieldHelpItems.find((i) => i.entityName === entityName && i.fieldName === fieldName);
    if (!fieldHelp) {
      this.appInsightsService.logMissingFieldHelp(entityName, fieldName);
      return undefined;
    }

    return fieldHelp;
  }
}
